import type { Catalog } from '../types';

/**
 * O perfil de outro jogador (canvas 3a/3b).
 *
 * Tudo aqui é na terceira pessoa, e o nome dele entra por parâmetro em vez de "ele" onde a frase
 * fica melhor assim — "Bruno leva 8" diz mais que "ele leva 8" numa tela que fala de duas pessoas
 * ao mesmo tempo.
 */
export const player = {
  'player.memberSince': { pt: 'Membro desde {when}', en: 'Member since {when}' },
  'player.friends': { pt: '✓ Amigos', en: '✓ Friends' },
  'player.you': { pt: 'Você', en: 'You' },
  'player.scratch': { pt: 'scratch', en: 'scratch' },

  'player.handicapIndex': { pt: 'Handicap index', en: 'Handicap index' },
  'player.basedOn': {
    pt: { one: 'Baseado em {count} volta', other: 'Baseado em {count} voltas' },
    en: { one: 'Based on {count} round', other: 'Based on {count} rounds' },
  },
  'player.strokesBetter': { pt: '{value} abaixo de você', en: '{value} below you' },
  'player.strokesWorse': { pt: '{value} acima de você', en: '{value} above you' },

  'player.noIndexYet': {
    pt: 'Você ainda não tem índice para comparar',
    en: 'You do not have an index to compare yet',
  },
  'player.theirGame': { pt: 'O jogo dele', en: 'Their game' },
  'player.rounds': { pt: 'Voltas', en: 'Rounds' },
  'player.roundsOver': {
    pt: { one: '{count} volta · {holes} buracos', other: '{count} voltas · {holes} buracos' },
    en: { one: '{count} round · {holes} holes', other: '{count} rounds · {holes} holes' },
  },
  'player.girAverage': { pt: 'GIR médio', en: 'Average GIR' },
  'player.fairway': { pt: 'Fairway', en: 'Fairway' },
  'player.handicap': { pt: 'Handicap', en: 'Handicap' },
  'player.inMonths': {
    pt: { one: 'em {count} mês', other: 'em {count} meses' },
    en: { one: 'over {count} month', other: 'over {count} months' },
  },
  'player.handicapAria': {
    pt: 'Handicap de {from} a {to}',
    en: 'Handicap from {from} to {to}',
  },
  'player.notEnoughForChart': {
    pt: 'Poucas voltas para desenhar a evolução',
    en: 'Not enough rounds to draw the trend',
  },
  'player.tookIt': {
    pt: { one: '{name} levou {count}', other: '{name} levou {count}' },
    en: { one: '{name} took {count}', other: '{name} took {count}' },
  },
  'player.recordRest': {
    pt: 'você {mine} · {ties} empate · no gross',
    en: 'you {mine} · {ties} tied · gross',
  },
  'player.recordNet': {
    pt: 'no net: {name} {theirs} · você {mine}',
    en: 'net: {name} {theirs} · you {mine}',
  },
  'player.chipTheyWon': { pt: '{name} levou · você {value}', en: '{name} won · you {value}' },
  'player.chipYouWon': { pt: 'Você levou · {value}', en: 'You won · {value}' },
  'player.chipTie': { pt: 'Empate em {value}', en: 'Tied at {value}' },
  'player.seeTogether': {
    pt: { one: 'Ver a {count} volta juntos', other: 'Ver as {count} voltas juntos' },
    en: { one: 'See the {count} round together', other: 'See the {count} rounds together' },
  },
  'player.averageScore': { pt: 'Score médio', en: 'Average score' },
  'player.bestRound': { pt: 'Melhor volta', en: 'Best round' },
  'player.puttsPerRound': { pt: 'Putts / volta', en: 'Putts / round' },
  'player.perHole': { pt: '{value} / buraco', en: '{value} / hole' },
  'player.bestDifferential': { pt: 'Melhor SD', en: 'Best SD' },

  'player.evolution': { pt: 'Evolução', en: 'Progress' },
  'player.seeAll': { pt: 'Ver tudo', en: 'See all' },

  'player.together': { pt: 'Voltas juntos', en: 'Rounds together' },
  'player.togetherCount': {
    pt: { one: '{count} volta com você', other: '{count} voltas com você' },
    en: { one: '{count} round with you', other: '{count} rounds with you' },
  },
  'player.roundsCount': {
    pt: { one: '{count} volta concluída', other: '{count} voltas concluídas' },
    en: { one: '{count} completed round', other: '{count} completed rounds' },
  },
  'player.ofWhichTogether': {
    pt: { one: '{count} com você', other: '{count} com você' },
    en: { one: '{count} with you', other: '{count} with you' },
  },
  'player.filterAll': { pt: 'Todas', en: 'All' },
  'player.filterTogether': { pt: 'Com você', en: 'With you' },
  'player.noRounds': { pt: 'Nenhuma volta por aqui', en: 'No rounds here' },
  'player.rowWhere': {
    pt: '{course} · {holes} buracos · Tee {tee}',
    en: '{course} · {holes} holes · {tee} tee',
  },
  'player.withYou': { pt: 'Com você · você {value}', en: 'With you · you {value}' },
  'player.showing': { pt: '{shown} de {total} voltas', en: '{shown} of {total} rounds' },
  'player.loadMore': {
    pt: { one: 'Carregar mais {count}', other: 'Carregar mais {count}' },
    en: { one: 'Load {count} more', other: 'Load {count} more' },
  },

  /* ------------------------------------------------ o desktop (canvas 3b) */

  'player.handicapEvolution': { pt: 'Evolução do handicap', en: 'Handicap progress' },
  'player.toPar': { pt: 'Score para o par', en: 'Score to par' },
  'player.meanAside': { pt: 'média {value}', en: 'average {value}' },
  'player.toParAria': {
    pt: { one: 'Score para o par em {count} volta', other: 'Score para o par em {count} voltas' },
    en: { one: 'Score to par over {count} round', other: 'Score to par over {count} rounds' },
  },
  'player.toParCaption': { pt: '{value} ao par', en: '{value} to par' },
  'player.countsToIndex': { pt: 'entra no índice', en: 'counts toward the index' },
  'player.togetherOfTotal': {
    pt: '{count} de {total} voltas dele foram com você',
    en: '{count} of their {total} rounds were with you',
  },
  'player.recordGross': { pt: 'Retrospecto no gross', en: 'Head to head, gross' },
  'player.tiesCount': {
    pt: { one: '{count} empate', other: '{count} empates' },
    en: { one: '{count} tie', other: '{count} ties' },
  },
  'player.tieLabel': { pt: 'empate', en: 'tie' },
  'player.netFlips': {
    pt: 'No net o retrospecto vira {theirs} – {mine}: a diferença de handicap inverte a conta.',
    en: 'In net the record becomes {theirs} – {mine}: the handicap gap flips it.',
  },
  'player.dateAndCourse': { pt: 'Data e campo', en: 'Date and course' },
  'player.difference': { pt: 'Diferença', en: 'Difference' },
  'player.aheadBy': { pt: '{name} +{value}', en: '{name} +{value}' },
  'player.open': { pt: 'Abrir', en: 'Open' },

  'player.private.title': { pt: 'Este jogador não está compartilhando', en: 'This player is not sharing' },
  'player.private.body': {
    pt: 'Ele desligou o compartilhamento de voltas com amigos. O interruptor vale nos dois sentidos — enquanto estiver assim, vocês não veem o jogo um do outro.',
    en: 'They turned off sharing rounds with friends. The switch works both ways — while it is off, neither of you sees the other’s game.',
  },
} as const satisfies Catalog;
